import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../services/api';
import './OnlineUsers.css';

interface OnlineUser {
  id: string;
  username: string;
  display_name: string | null;
  trust_rating: number;
  is_admin: boolean;
  city: string | null;
  country: string | null;
  state_province: string | null;
}

interface AllUser {
  id: string;
  username: string;
  display_name: string | null;
  trust_rating: number;
  is_admin: boolean;
  city: string | null;
  state_province: string | null;
  country: string | null;
}

const PROTECTED_USERNAMES = ['admin', 'Stickkzz'];

const OnlineUsers: React.FC = () => {
  const [users, setUsers] = useState<OnlineUser[]>([]);
  const [offlineUsers, setOfflineUsers] = useState<AllUser[]>([]);
  const [totalOnline, setTotalOnline] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const [onlineRes, allRes] = await Promise.all([
        adminAPI.getOnlineUsers(),
        adminAPI.getAllUsers(1, 1000),
      ]);

      const onlineUsers: OnlineUser[] = onlineRes.data.users || [];
      setUsers(onlineUsers);
      setTotalOnline(onlineRes.data.total_online || 0);

      const allUsers: AllUser[] = allRes.data.users || [];
      const onlineIds = new Set(onlineUsers.map((u) => u.id));
      setOfflineUsers(allUsers.filter((u) => !onlineIds.has(u.id)));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  const handlePromote = async (userId: string, name: string) => {
    if (!window.confirm(`Promote "${name}" to admin?`)) return;
    setActionId(userId);
    try {
      await adminAPI.promoteToAdmin(userId);
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to promote user');
    } finally {
      setActionId(null);
    }
  };

  const handleDemote = async (userId: string, name: string) => {
    if (!window.confirm(`Remove admin from "${name}"?`)) return;
    setActionId(userId);
    try {
      await adminAPI.demoteAdmin(userId);
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to demote user');
    } finally {
      setActionId(null);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const getRatingColor = (rating: number) => {
    if (rating >= 7) return '#00ba7c';
    if (rating >= 4) return '#f5a623';
    return '#e0245e';
  };

  const renderAdminAction = (user: { id: string; username: string; display_name: string | null; is_admin: boolean }) => {
    const isProtected = PROTECTED_USERNAMES.includes(user.username);
    const name = user.display_name || user.username;

    if (user.is_admin) {
      return (
        <button
          className="demote-btn"
          disabled={isProtected || actionId === user.id}
          onClick={() => handleDemote(user.id, name)}
          title={isProtected ? 'Protected user' : 'Remove admin'}
        >
          {actionId === user.id ? 'Removing...' : 'Remove Admin'}
        </button>
      );
    }

    return (
      <button
        className="promote-btn"
        disabled={actionId === user.id}
        onClick={() => handlePromote(user.id, name)}
      >
        {actionId === user.id ? 'Promoting...' : 'Make Admin'}
      </button>
    );
  };

  return (
    <div className="online-users-page">
      <div className="page-header">
        <h2>Online Users</h2>
        <span className="online-badge">{totalOnline} online</span>
      </div>

      {error && <div className="feeds-error"><p>{error}</p></div>}

      {loading ? (
        <div className="feeds-loading"><p>Loading users...</p></div>
      ) : (
        <>
          <div className="feeds-table-container">
            <table className="feeds-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Rating</th>
                  <th>City</th>
                  <th>State / Province</th>
                  <th>Country</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      {user.display_name || user.username}
                      {user.is_admin && <span className="admin-tag">Admin</span>}
                    </td>
                    <td>
                      <span className="rating-badge" style={{ color: getRatingColor(user.trust_rating) }}>
                        {user.trust_rating}/10
                      </span>
                    </td>
                    <td>{user.city || 'Unknown'}</td>
                    <td>{user.state_province || 'Unknown'}</td>
                    <td>{user.country || 'Unknown'}</td>
                    <td>{renderAdminAction(user)}</td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)' }}>
                      No users currently online.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="section-header">
            <h3>Offline Users</h3>
            <span className="offline-badge">{offlineUsers.length} offline</span>
          </div>

          <div className="feeds-table-container">
            <table className="feeds-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Rating</th>
                  <th>City</th>
                  <th>State / Province</th>
                  <th>Country</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {offlineUsers.map((user) => (
                  <tr key={user.id}>
                    <td>
                      {user.display_name || user.username}
                      {user.is_admin && <span className="admin-tag">Admin</span>}
                    </td>
                    <td>
                      <span className="rating-badge" style={{ color: getRatingColor(user.trust_rating) }}>
                        {user.trust_rating}/10
                      </span>
                    </td>
                    <td>{user.city || 'Unknown'}</td>
                    <td>{user.state_province || 'Unknown'}</td>
                    <td>{user.country || 'Unknown'}</td>
                    <td>{renderAdminAction(user)}</td>
                  </tr>
                ))}
                {offlineUsers.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)' }}>
                      All users are currently online.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default OnlineUsers;
